import React from "react";
import { addMonths, format, isValid, parseISO } from "date-fns";
import InputLine from "~/components/RegisterForm/InputLine";

const money = (value) => `$${(Number(value) || 0).toFixed(2)}`;

const formatDate = (value) => {
  if (!value) return "-";
  const parsed = parseISO(value);
  return isValid(parsed) ? format(parsed, "MM/dd/yyyy") : value;
};

function installmentDueDates(startDate, firstTuitionDueDate) {
  const start = parseISO(startDate || "");
  if (!isValid(start)) return [null, null, null];
  const firstDue = firstTuitionDueDate ? parseISO(firstTuitionDueDate) : start;
  if (!isValid(firstDue)) return [null, null, null];
  return [firstDue, addMonths(firstDue, 1), addMonths(firstDue, 2)];
}

function splitInstallments(tuitionTotal) {
  const base = Math.floor((tuitionTotal / 3) * 100) / 100;
  const first = Number((tuitionTotal - base * 2).toFixed(2));
  return [first, base, base];
}

function GlobalSessionSummary({
  session = null,
  invoices = [],
  priceLists = null,
  simulatedTotals = null,
  hourlyPreview = null,
  pricingMode = "fixed",
  paymentType = "full",
  firstTuitionDueDate = null,
  sessionStartDate = null,
}) {
  if (!session) return null;

  const tuitionInvoices = invoices.filter(
    (invoice) => invoice.type_detail === "Tuition fee"
  );

  const isHourly = pricingMode === "hourly";
  const resolvedPaymentType = isHourly ? "full" : paymentType;
  const registrationFee = Number(priceLists?.registration_fee || 0);

  const tuitionOriginal = isHourly
    ? Number(hourlyPreview?.tuition ?? session.tuition_amount ?? 0)
    : Number(priceLists?.tuition || 0);
  const totalDiscount = isHourly
    ? 0
    : Number(simulatedTotals?.totalDiscount || 0);
  const tuitionAfterDiscount = Math.max(
    0,
    Number((tuitionOriginal - totalDiscount).toFixed(2))
  );
  const grandTotal = isHourly
    ? Number((registrationFee + tuitionAfterDiscount).toFixed(2))
    : simulatedTotals?.totalTuition ??
      tuitionOriginal + registrationFee + Number(priceLists?.book || 0);

  const dueDates = installmentDueDates(sessionStartDate, firstTuitionDueDate);
  const installments = splitInstallments(tuitionAfterDiscount);

  return (
    <>
      <InputLine title="Payment Summary">
        <div className="w-full flex flex-col gap-2 text-xs bg-blue-50 border border-blue-200 rounded-md p-3">
          {isHourly && (
            <>
              <div className="flex justify-between">
                <span className="text-gray-600">Total hours:</span>
                <span className="font-medium">
                  {hourlyPreview?.total_hours ?? session.total_hours ?? 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Rate per hour:</span>
                <span className="font-medium">
                  {money(
                    hourlyPreview?.rate_per_hour ??
                      session.rate_per_hour ??
                      0
                  )}
                </span>
              </div>
            </>
          )}
          <div className="flex justify-between">
            <span className="text-gray-600">Registration Fee:</span>
            <span className="font-medium">{money(registrationFee)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Tuition:</span>
            <span className="font-medium">{money(tuitionOriginal)}</span>
          </div>
          {totalDiscount > 0 && (
            <div className="flex justify-between text-green-700">
              <span>Discount:</span>
              <span className="font-medium">-{money(totalDiscount)}</span>
            </div>
          )}
          <div className="flex justify-between border-t pt-1 font-semibold">
            <span>Grand Total{isHourly ? " (paid in full)" : ""}:</span>
            <span>{money(grandTotal)}</span>
          </div>

          {resolvedPaymentType === "installments" && (
            <div className="mt-1 border-t pt-2 flex flex-col gap-1 text-gray-600">
              <span className="font-semibold text-gray-700">
                Installment Preview (Tuition only):
              </span>
              <div className="flex justify-between">
                <span>
                  1st (tuition due date)
                  {dueDates[0] ? ` - ${format(dueDates[0], "MM/dd/yyyy")}` : ""}:
                </span>
                <span>{money(installments[0])}</span>
              </div>
              <div className="flex justify-between">
                <span>
                  2nd (+1 month)
                  {dueDates[1] ? ` - ${format(dueDates[1], "MM/dd/yyyy")}` : ""}:
                </span>
                <span>{money(installments[1])}</span>
              </div>
              <div className="flex justify-between">
                <span>
                  3rd (+2 months)
                  {dueDates[2] ? ` - ${format(dueDates[2], "MM/dd/yyyy")}` : ""}:
                </span>
                <span>{money(installments[2])}</span>
              </div>
            </div>
          )}

          <p className="border-t pt-2 text-gray-500">
            These are the values that will be charged once you save. The
            invoices below are the ones that exist today.
          </p>
        </div>
      </InputLine>

      <InputLine title="Invoices">
        <div className="w-full overflow-x-auto">
          {invoices.length === 0 ? (
            <p className="text-xs text-gray-400 text-left">
              No invoices found.
            </p>
          ) : (
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-gray-100 text-gray-600">
                  <th className="text-left px-2 py-1">Type</th>
                  <th className="text-left px-2 py-1">Due Date</th>
                  <th className="text-right px-2 py-1">Amount</th>
                  <th className="text-right px-2 py-1">Balance</th>
                  <th className="text-left px-2 py-1">Status</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr
                    key={invoice.id}
                    className="border-b even:bg-gray-50 text-left"
                  >
                    <td className="px-2 py-1">
                      {invoice.type_detail}
                      {invoice.type_detail === "Tuition fee" &&
                      tuitionInvoices.length > 1
                        ? ` (${tuitionInvoices.indexOf(invoice) + 1}/${
                            tuitionInvoices.length
                          })`
                        : ""}
                    </td>
                    <td className="px-2 py-1">{formatDate(invoice.due_date)}</td>
                    <td className="px-2 py-1 text-right">
                      {money(invoice.total)}
                    </td>
                    <td className="px-2 py-1 text-right">
                      {money(invoice.balance)}
                    </td>
                    <td className="px-2 py-1">
                      <span
                        className={
                          invoice.status === "Pending"
                            ? "text-red-500"
                            : "text-green-600 font-semibold"
                        }
                      >
                        {invoice.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </InputLine>
    </>
  );
}

export default GlobalSessionSummary;
